import { StandardButton } from 'base/StandardButton/StandardButton';
import { PRODUCT } from 'constant/RoutesUrls';
import { t } from 'i18next';
import React, { useCallback, useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import { Card } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  capitalizeFirstLetter,
  checkAccess,
  convertLogsToTimeline,
  convertToLabelValue,
  parseIntoNumber
} from 'utils/helpers';
import {
  ApplicationTypeEnum,
  LogEntry,
  Meta,
  OptionI,
  paramsI,
  ProductFormInterface
} from '../../../../types/interface';
import { useAppDispatch } from '../../../redux/HooksRedux';
import * as productAction from '../../../redux/product/productAction';
import {
  errorNotification,
  successNotification
} from 'base/notifications/Notifications';
import StandardSelect from 'base/StandardSelect/StandardSelect';
import { ProductValidationSet } from 'utils/validations/ValidationsSet/ProductValidationSet';
import StandardCheckbox from 'base/StandardCheckbox/StandardCheckbox';
import StandardInput from 'base/StandardInput/StandardInput';
import validationFunctions from 'utils/validationUtils';
import ConditionalRender from 'base/ConditionalRender/ConditionalRender';
import { StandardLoader } from 'base/Loader/Loader';
import { AxiosError, AxiosResponse } from 'axios';
import {
  CUSTOM_REFUNDABLE,
  HEALTH_ENUM,
  logPerPage,
  LogType,
  modules,
  NOT_REFUNDABLE,
  REFUNDABLE,
  RefundPolicy,
  SCHOOL_ACTIVITY_OF_CHILD_ENUM
} from 'utils/enums/misc';
import { StandardRadio } from 'base/StandardRadio/StandardRadio';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import DeleteModel from 'base/DeleteModal/DeleteModal';
import { Dropdown } from 'react-bootstrap';
import { ApplicationTypes, refundOptions, tabData } from 'utils/staticData';
import { getRequestOptionById } from '../../../redux/application/applicationAction';
import {
  DYNAMIC_APPLICATION_EDUCATION_ID,
  DYNAMIC_APPLICATION_HEALTH_ID
} from 'utils/enums/dynamic_misc';
import StandardTab from 'base/StandardTab/StandardTab';
import InfinityScroll from 'base/InfonityScroll/InfinityScroll';
import { Timeline } from 'data/timelineData';
import BasicTimeline from 'components/timelines/BasicTimeline';
import * as logAction from '../../../redux/logs/logAction';

type RequestOptionForDifferentApplication = Map<ApplicationTypeEnum, OptionI[]>;

const ProductForm = () => {
  const navigate = useNavigate();
  const [URLSearchParams] = useSearchParams();
  const productId = URLSearchParams.get('productId');
  const dispatch = useAppDispatch();
  const userPermission = useSelector(
    (state: RootState) => state.userProfile.permission
  );

  const [requestOptionList, setRequestOptionList] =
    useState<RequestOptionForDifferentApplication>();
  const [showError, setShowError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [deleteProduct, setDeleteProduct] = useState<{
    product_id: number;
    showPopup: boolean;
  }>({
    product_id: 0,
    showPopup: false
  });

  // Logs states
  const [logCurrentPage, setLogCurrentPage] = useState(1);
  const [logs, setLogs] = useState<Timeline[]>([]);
  const [activeTab, setActiveTab] = useState<LogType | null>(LogType.FORM);
  const [hasMoreLogs, setHasMoreLogs] = useState(false);
  const [logLoading, setLogLoading] = useState(false);

  const [formData, setFormData] = useState<ProductFormInterface>({
    application_type: null,
    productName: '',
    request_option_ids: [],
    productNameGerman: '',
    productDescriptionGerman: '',
    productDescription: '',
    is_allow_negative_amount: false,
    is_archived: false,
    refund_policy: RefundPolicy.FULL_OPTIONAL_SV,
  });
  const [refundableOptions, setRefundableOptions] =
    useState<OptionI[]>(refundOptions);

  const getAllApplicationTypeOption = async (
    applicationType: ApplicationTypeEnum
  ) => {
    const params: paramsI & { application_type: ApplicationTypeEnum } = {
      keyword: '',
      page: 1,
      limit: 1000,
      is_archived: false,
      application_type: applicationType
    };
    try {
      if (requestOptionList && requestOptionList.has(applicationType)) {
        return;
      }
      let response: any;

      switch (applicationType) {
        case HEALTH_ENUM:
          response = await dispatch(
            getRequestOptionById(DYNAMIC_APPLICATION_HEALTH_ID as number)
          );
          break;

        case SCHOOL_ACTIVITY_OF_CHILD_ENUM:
          response = await dispatch(
            getRequestOptionById(DYNAMIC_APPLICATION_EDUCATION_ID as number)
          );
          break;

        default:
          response = await dispatch(
            getRequestOptionById(DYNAMIC_APPLICATION_HEALTH_ID as number)
          );
          break;
      }

      if (response.status === 200) {
        const convertValue = convertToLabelValue(
          response.data.application_request_options,
          'name',
          'application_request_option_id'
        );

        setRequestOptionList(prev => {
          const newMap = new Map(prev);
          newMap.set(applicationType, convertValue);
          return newMap;
        });
      } else {
        errorNotification(response?.data?.message);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleSubmit = async (e: React.FormEvent, isArchive?: boolean) => {
    e.preventDefault();
    setIsLoading(true);

    let validations = { ...ProductValidationSet };

    if (formData.application_type?.includes(SCHOOL_ACTIVITY_OF_CHILD_ENUM)) {
      // @ts-expect-error "request_option_ids" is not required for this application type
      delete validations.request_option_ids;
    }

    const validate: boolean = validationFunctions.checkFormValidity(
      {
        ...formData,
        is_archived:
          typeof isArchive === 'boolean' ? isArchive : formData.is_archived
      },
      validations
    );

    if (!validate) {
      setShowError(true);
      setIsLoading(false);
      return;
    }

    if (productId && checkAccess([modules.PRODUCT.edit], userPermission)) {
      try {
        const response = await dispatch(
          productAction.updateProduct(Number(productId), {
            ...formData,
            is_archived:
              typeof isArchive === 'boolean' ? isArchive : formData.is_archived
          })
        );

        if (response.status === 200) {
          successNotification(response.data?.message);
          navigate(PRODUCT);
          setIsLoading(false);
        } else {
          errorNotification(response.data?.message);
          setIsLoading(false);
        }
      } catch (error) {
        if (error instanceof Error) {
          errorNotification(error.message);
        } else if (
          error instanceof AxiosError &&
          error?.response?.data?.message
        ) {
          errorNotification(error?.response?.data?.message);
        }
        console.log(error);
        setIsLoading(false);
      }
    } else if (checkAccess([modules.PRODUCT.create], userPermission)) {
      try {
        const response = await dispatch(productAction.createProduct(formData));

        if (response?.status === 201) {
          successNotification(response.data?.message);
          navigate(PRODUCT);
          setIsLoading(false);
        } else {
          errorNotification(response?.data?.message);
          setIsLoading(false);
        }
      } catch (error) {
        console.log(error);
        setIsLoading(false);
      }
    } else {
      navigate(PRODUCT);
      setIsLoading(false);
    }
  };

  const getSingleProduct = async () => {
    if (!checkAccess([modules.PRODUCT.edit], userPermission)) {
      navigate(PRODUCT);
      return;
    }
    setIsLoading(true);
    try {
      const response = await dispatch(
        productAction.getSingleProduct(Number(productId))
      );
      if (response?.status === 200) {
        setFormData({
          application_type: response.data.final_data.product.application_type,
          productName: response.data.final_data.product.name.en,
          productDescription: response.data.final_data.product.description.en,
          productNameGerman: response.data.final_data.product.name.de,
          request_option_ids:
            response.data.final_data.product.application_option_ids,
          productDescriptionGerman:
            response.data.final_data.product.description.de,
          is_allow_negative_amount:
            response.data.final_data.product?.is_allow_negative_amount,
          is_archived: response.data.final_data.product?.is_archived,
          refund_policy: response.data.final_data.product?.refund_policy
        });
        response.data.final_data.product.application_type.forEach(
          (applicationTypeEnum: ApplicationTypeEnum) => {
            getAllApplicationTypeOption(applicationTypeEnum);
          }
        );
        setIsLoading(false);
      } else {
        errorNotification(response?.data?.message);
        navigate(PRODUCT);
        setIsLoading(false);
      }
    } catch (error) {
      console.log(error);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (productId) {
      getSingleProduct();
    }
  }, []);

  const handleDeleteProduct = async () => {
    setIsLoading(true);
    try {
      const response = await dispatch(
        productAction.deleteProduct(deleteProduct.product_id)
      );
      if (response?.status === 200) {
        successNotification(response.data?.message);
        setDeleteProduct({ product_id: 0, showPopup: false });
        navigate(PRODUCT);
        setIsLoading(false);
      } else {
        errorNotification(response?.data?.message);
        setDeleteProduct({ product_id: 0, showPopup: false });
        setIsLoading(false);
      }
    } catch (error) {
      setDeleteProduct({ product_id: 0, showPopup: false });
      console.log(error);
      setIsLoading(false);
    }
  };

  const renderDeletePopup = () => {
    if (deleteProduct.showPopup) {
      return (
        <DeleteModel
          onCancel={() => setDeleteProduct({ product_id: 0, showPopup: false })}
          onConfirm={handleDeleteProduct}
        />
      );
    }
  };

  useEffect(() => {
    setRefundableOptions(prev => {
      if (formData.application_type?.includes(SCHOOL_ACTIVITY_OF_CHILD_ENUM)) {
        const allowedpolicy = [RefundPolicy.FULL_OPTIONAL_SV, RefundPolicy.NON_REFUNDABLE]
        setFormData(prev => ({ ...prev, refund_policy: RefundPolicy.FULL_OPTIONAL_SV }))
        return prev.filter(option => allowedpolicy.includes(option.value as RefundPolicy));
      } else {
        return refundOptions;
      }
    });
  }, [formData.application_type]);

  const generateRequestOptionList = (): OptionI[] | [] => {
    if (!formData.application_type?.length || !requestOptionList?.size) {
      return [];
    }
    const requests = formData.application_type.map(applicationType => {
      if (requestOptionList.has(applicationType)) {
        const requestOption = requestOptionList.get(applicationType);
        const uniqueRequestOption = new Set<OptionI>(
          requestOption?.map(option => ({
            label: option.label,
            value: option.value
          })) || []
        );
        return requestOption as OptionI[];
      } else {
        return [];
      }
    });

    if (requests && requests.flat()) {
      const uniqueMap = new Map();
      requests.flat().forEach(option => {
        if (!uniqueMap.has(option.value)) {
          uniqueMap.set(option.value, option);
        }
      });
      return Array.from(uniqueMap.values());
    } else {
      return [];
    }
  };

  const getProductLogs = useCallback(
    async (roleId: number) => {
      try {
        const params: paramsI = {
          page: 1,
          limit: logPerPage * logCurrentPage,
          keyword: ''
        };
        const response: AxiosResponse<{ logs: LogEntry[]; meta: Meta }> =
          await dispatch(logAction.getLogByProductId(roleId, params));
        const timeLineData = convertLogsToTimeline(response.data.logs);
        if (params.limit >= response.data.meta.totalRecords) {
          setHasMoreLogs(false);
        } else {
          setHasMoreLogs(true);
        }

        setLogs(timeLineData);
      } catch (error) {
        console.error(error);
      } finally {
        setLogLoading(false);
      }
    },
    [logCurrentPage, logPerPage]
  );

  useEffect(() => {
    if (productId && activeTab === LogType.LOGS) {
      getProductLogs(Number(productId));
    }
  }, [productId, logCurrentPage]); // Remove activeTab from dependencies

  // Add a separate useEffect to handle tab changes:
  useEffect(() => {
    if (activeTab === LogType.LOGS && logs.length === 0) {
      getProductLogs(Number(productId));
    }
  }, [activeTab]);

  return (
    <Card>
      <ConditionalRender condition={!isLoading} fallBack={<StandardLoader />}>
        <Form onSubmit={handleSubmit}>
          <Card.Header className="py-2">
            <div className="row justify-content-between align-items-center gap-2">
              <div className="w-auto">
                <h3>{capitalizeFirstLetter(t('header.text.text_3'))} </h3>
              </div>
              <div className="d-flex gap-2 w-auto">
                <StandardButton
                  onClick={() => {
                    navigate(PRODUCT);
                  }}
                  buttonText={t('general.common_text.text_15')}
                  variant="outline-danger"
                />
                <StandardButton
                  onClick={handleSubmit}
                  type="submit"
                  buttonText={
                    productId
                      ? t('general.common_text.text_18')
                      : t('general.common_text.text_2')
                  }
                />
                <ConditionalRender condition={!!productId}>
                  <Dropdown
                    id="scroll_in_table"
                    placement="auto"
                    className="position-static"
                  >
                    <Dropdown.Toggle className="no-padding no-margin p-0 d-flex bg-transparent">
                      <StandardButton
                        buttonText={capitalizeFirstLetter(
                          t('general.common_text.text_8')
                        )}
                        onClick={() => {}}
                        variant="outline-danger"
                      />
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="p-0">
                      <ConditionalRender
                        condition={checkAccess(
                          [modules.PRODUCT.edit],
                          userPermission
                        )}
                      >
                        <Dropdown.Item
                          className="pe-0 border-bottom"
                          onClick={e => {
                            handleSubmit(e, !formData.is_archived);
                          }}
                        >
                          <span className="d-flex fs-16 p-0 font-normal">
                            <p className="mb-0">
                              <ConditionalRender
                                condition={formData.is_archived}
                                fallBack={t('common_table.text_5')}
                              >
                                {t('common_table.text_4')}
                              </ConditionalRender>
                            </p>
                          </span>
                        </Dropdown.Item>
                      </ConditionalRender>
                      <ConditionalRender
                        condition={checkAccess(
                          [modules.PRODUCT.delete],
                          userPermission
                        )}
                      >
                        <Dropdown.Item
                          onClick={() => {
                            setDeleteProduct({
                              product_id: parseIntoNumber(productId),
                              showPopup: true
                            });
                          }}
                        >
                          <span className="d-flex fs-16 font-normal p-0">
                            <p className="mb-0">
                              {capitalizeFirstLetter(
                                t('general.common_text.text_14')
                              )}
                            </p>
                          </span>
                        </Dropdown.Item>
                      </ConditionalRender>
                    </Dropdown.Menu>
                  </Dropdown>
                </ConditionalRender>
              </div>
            </div>
          </Card.Header>
          <Card.Body>
            <ConditionalRender condition={!!productId}>
              <div>
                <StandardTab
                  activeKey={activeTab}
                  data={tabData}
                  onChange={setActiveTab}
                />
              </div>
            </ConditionalRender>
            <ConditionalRender
              condition={activeTab === LogType.FORM}
              fallBack={
                <InfinityScroll
                  childrenIdPrefix="product-log"
                  currentPage={logCurrentPage}
                  setCurrentPage={setLogCurrentPage}
                  dataLength={logs.length}
                  hasMore={hasMoreLogs}
                  limitPerPage={logPerPage}
                  loading={logLoading}
                  setLoading={setLogLoading}
                >
                  <div>
                    {logs.map(i => {
                      return (
                        <div key={i.id}>
                          <h4 className="py-2 border-y mb-3 ">{i.date}</h4>
                          <BasicTimeline
                            data={i.items}
                            idPrefix="product-log"
                          />
                        </div>
                      );
                    })}
                  </div>
                </InfinityScroll>
              }
            >
              <div>
                <div className="row">
                  <div className="col-12 col-md-6">
                    <StandardSelect
                      value={formData.application_type}
                      formClassName=""
                      id=""
                      label={t('product_form.placeholder.text_2')}
                      placeholder={t('product_form.placeholder.text_2')}
                      onChange={e => {
                        let filteredApplicationRequest = [];
                        if (requestOptionList && requestOptionList?.size > 0) {
                          filteredApplicationRequest = e.flatMap(
                            (applicationType: ApplicationTypeEnum) => {
                              const options =
                                requestOptionList?.get(applicationType) || [];
                              return formData.request_option_ids.filter(
                                requestId =>
                                  options.some(
                                    option => option.value === requestId
                                  )
                              );
                            }
                          );
                          setFormData({
                            ...formData,
                            application_type: e,
                            request_option_ids: filteredApplicationRequest
                          });
                        } else {
                          setFormData({
                            ...formData,
                            application_type: e
                          });
                        }
                        getAllApplicationTypeOption([...e].pop());
                      }}
                      showError={showError}
                      options={ApplicationTypes}
                      validations={ProductValidationSet.application_type}
                      isMulti={true}
                      isClearable={true}
                    />
                  </div>
                  <div className="col-12 col-md-6">
                    <StandardSelect
                      value={formData.request_option_ids}
                      placeholder={t('product_form.placeholder.text_4')}
                      onChange={e =>
                        setFormData({ ...formData, request_option_ids: e })
                      }
                      showError={
                        formData.application_type?.includes(
                          SCHOOL_ACTIVITY_OF_CHILD_ENUM
                        )
                          ? false
                          : showError
                      }
                      validations={
                        formData.application_type?.includes(
                          SCHOOL_ACTIVITY_OF_CHILD_ENUM
                        )
                          ? []
                          : ProductValidationSet.request_option_ids
                      }
                      className={''}
                      isMulti={true}
                      options={generateRequestOptionList()}
                      formClassName=""
                      id=""
                      label={t('product_form.placeholder.text_4')}
                      disabled={!formData.application_type?.length}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-12 col-md-12 gap-4 mt-2">
                    <StandardRadio
                      outerClassName="d-flex align-items-center"
                      className=""
                      onChange={value => {
                        setFormData({
                          ...formData,
                          refund_policy: value as RefundPolicy
                        });
                      }}
                      options={refundableOptions}
                      showError={showError}
                      value={formData.refund_policy}
                    />
                    <StandardCheckbox
                      label={t('product_form.placeholder.text_5')}
                      isChecked={formData.is_allow_negative_amount}
                      setChecked={(e: boolean) =>
                        setFormData({
                          ...formData,
                          is_allow_negative_amount: e
                        })
                      }
                      className=""
                      showError={showError}
                    />
                  </div>
                </div>
              </div>
              <div>
                <div className="row">
                  <div className="col-12 col-md-6">
                    <StandardInput
                      type="text"
                      value={formData.productName}
                      placeholder={t('product_form.placeholder.text_8')}
                      onChange={e =>
                        setFormData({ ...formData, productName: e?.toString() })
                      }
                      showError={showError}
                      validations={ProductValidationSet.productName}
                      className=""
                      id=""
                      label={t('product_form.placeholder.text_8')}
                    />
                  </div>
                  <div className="col-12 col-md-6">
                    <StandardInput
                      type="text"
                      value={formData.productNameGerman}
                      placeholder={t('product_form.placeholder.text_9')}
                      onChange={e =>
                        setFormData({
                          ...formData,
                          productNameGerman: e?.toString()
                        })
                      }
                      showError={showError}
                      className=""
                      validations={ProductValidationSet.productNameGerman}
                      id=""
                      label={t('product_form.placeholder.text_9')}
                    />
                  </div>
                </div>
              </div>
              <div>
                <div className="row">
                  <div className="col-12 col-md-6">
                    <StandardInput
                      className="font-weight-medium text-fern-flower extra-small"
                      value={formData.productDescription as string}
                      onChange={e => {
                        setFormData({
                          ...formData,
                          productDescription: e?.toString().trimStart()
                        });
                      }}
                      placeholder={t('product_form.placeholder.text_10')}
                      id=""
                      label={t('product_form.placeholder.text_10')}
                      showError={showError}
                      validations={[]}
                      type="text"
                      as={'textarea'}
                    />
                  </div>
                  <div className="col-12 col-md-6">
                    <StandardInput
                      className="font-weight-medium text-fern-flower extra-small"
                      value={formData.productDescriptionGerman as string}
                      onChange={e => {
                        setFormData({
                          ...formData,
                          productDescriptionGerman: e?.toString()?.trimStart()
                        });
                      }}
                      placeholder={t('product_form.placeholder.text_11')}
                      id=""
                      label={t('product_form.placeholder.text_11')}
                      showError={showError}
                      type="text"
                      validations={[]}
                      as={'textarea'}
                    />
                  </div>
                  <div className="col-4"></div>
                </div>
              </div>
            </ConditionalRender>
          </Card.Body>
        </Form>
      </ConditionalRender>
      {renderDeletePopup()}
    </Card>
  );
};

export default ProductForm;
